/**
 * RatingControls — Helpful / Not helpful on an answered message (BR-34), plus the optional "What was
 * wrong?" comment that follows a Not helpful (US-15). The comment can be sent or skipped; it never blocks
 * the rating, which is saved the moment a thumb is pressed.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { HELP_RATING_COMMENT_MAX_CHARS, type HelpBotMessage, type HelpRating } from '@resida/api-types/help';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useRateHelpMessage } from '../../lib/api.help';

const RATING_BUTTON_CLASS =
  'flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/** closed = nothing to show; asking = the comment field is open; saved = the comment was stored. */
type CommentState = 'closed' | 'asking' | 'saved';

interface RatingControlsProps {
  message: HelpBotMessage;
  onRated: (messageId: string, rating: HelpRating, offerEscalation: boolean) => void;
}

function RatingCommentForm({
  messageId,
  send,
  onSaved,
  onSkip,
}: {
  messageId: string;
  /** Owned by RatingControls so the thumbs can be held while the comment request is in flight. */
  send: ReturnType<typeof useRateHelpMessage>;
  onSaved: () => void;
  onSkip: () => void;
}) {
  const [comment, setComment] = useState('');
  const fieldId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  // The field opens right after the thumb is pressed, so keyboard users land in it; the whole form
  // is scrolled into the thread so the buttons under the field are not left behind the composer.
  useEffect(() => {
    fieldRef.current?.focus({ preventScroll: true });
    formRef.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  const trimmed = comment.trim();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed === '' || send.isPending) return;
    send.mutate({ messageId, value: 'down', comment: trimmed }, { onSuccess: onSaved });
  };

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
      data-testid="help-rating-comment"
    >
      <label htmlFor={fieldId} className="text-[13px] font-semibold text-foreground">
        What was wrong? <span className="font-normal text-muted-foreground">(optional)</span>
      </label>
      <textarea
        id={fieldId}
        ref={fieldRef}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        maxLength={HELP_RATING_COMMENT_MAX_CHARS}
        rows={3}
        className="min-h-[72px] w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
        data-testid="help-rating-comment-input"
      />
      <span className="self-end text-xs text-muted-foreground" data-testid="help-rating-comment-counter">
        {comment.length} / {HELP_RATING_COMMENT_MAX_CHARS}
      </span>
      {send.isError && (
        <p role="alert" className="text-xs text-destructive" data-testid="help-rating-comment-error">
          We couldn't save your comment. Try again.
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={trimmed === '' || send.isPending} data-testid="help-rating-comment-send">
          Send feedback
        </Button>
        <Button type="button" variant="ghost" onClick={onSkip} disabled={send.isPending} data-testid="help-rating-comment-skip">
          Skip
        </Button>
      </div>
    </form>
  );
}

export function RatingControls({ message, onRated }: RatingControlsProps) {
  const rate = useRateHelpMessage();
  const sendComment = useRateHelpMessage();
  const busy = rate.isPending || sendComment.isPending;
  const [commentState, setCommentState] = useState<CommentState>('closed');
  const downButton = useRef<HTMLButtonElement>(null);

  const choose = (value: HelpRating) => {
    if (message.rating === value || busy) return;
    rate.mutate(
      { messageId: message.id, value },
      {
        onSuccess: (result) => {
          onRated(message.id, result.rating, result.offerEscalation);
          setCommentState(result.rating === 'down' ? 'asking' : 'closed');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label="Rate this answer" className="flex items-center gap-1" data-testid="help-rating">
        <button
          type="button"
          aria-label="Helpful"
          aria-pressed={message.rating === 'up'}
          disabled={busy}
          onClick={() => choose('up')}
          data-testid={`help-rate-up-${message.id}`}
          className={cn(RATING_BUTTON_CLASS, message.rating === 'up' && 'bg-accent text-foreground')}
        >
          <ThumbsUp className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          ref={downButton}
          type="button"
          aria-label="Not helpful"
          aria-pressed={message.rating === 'down'}
          disabled={busy}
          onClick={() => choose('down')}
          data-testid={`help-rate-down-${message.id}`}
          className={cn(RATING_BUTTON_CLASS, message.rating === 'down' && 'bg-accent text-foreground')}
        >
          <ThumbsDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {rate.isError && (
        <p role="alert" className="text-xs text-destructive" data-testid="help-rating-error">
          We couldn't save your rating. Try again.
        </p>
      )}
      {commentState === 'asking' && (
        <RatingCommentForm
          messageId={message.id}
          send={sendComment}
          onSaved={() => setCommentState('saved')}
          onSkip={() => {
            setCommentState('closed');
            downButton.current?.focus();
          }}
        />
      )}
      {commentState === 'saved' && (
        <p role="status" className="text-xs text-muted-foreground" data-testid="help-rating-comment-saved">
          Thanks. Your feedback was saved.
        </p>
      )}
    </div>
  );
}
